import React, {useEffect, useState} from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Grid,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {getMortalityRecords, createMortalityRecord, updateMortalityRecord} from "../../api/pond";
import {useTranslation} from "react-i18next";

interface MortalityTabProps {
  pondId: string;
  cycleId?: string;
  isCycleActive?: boolean;
}

const MortalityTab: React.FC<MortalityTabProps> = ({pondId, cycleId, isCycleActive = true}) => {
  const {t} = useTranslation();
  const [mortalityRecords, setMortalityRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [formData, setFormData] = useState({
    date: "",
    age: 0,
    basedOn: "Count",
    quantity: 0,
    weight: 0,
    size: 0,
    multiplier: 1,
    notes: "",
  });

  useEffect(() => {
    fetchMortalityRecords();
  }, [pondId, cycleId]);

  const fetchMortalityRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getMortalityRecords(pondId, cycleId);
      setMortalityRecords(data.data || data);
    } catch (err) {
      setError("Failed to load mortality records");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (record?: any) => {
    if (record) {
      setEditingRecord(record);
      setFormData({
        date: record.date ? new Date(record.date).toISOString().split("T")[0] : "",
        age: record.age,
        basedOn: record.basedOn,
        quantity: record.quantity,
        weight: record.weight || 0,
        size: record.size || 0,
        multiplier: record.multiplier || 1,
        notes: record.notes || "",
      });
    } else {
      setEditingRecord(null);
      setFormData({
        date: new Date().toISOString().split("T")[0],
        age: 0,
        basedOn: "Count",
        quantity: 0,
        weight: 0,
        size: 0,
        multiplier: 1,
        notes: "",
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
  };

  const handleSave = async () => {
    try {
      if (editingRecord) {
        await updateMortalityRecord(editingRecord.id, formData);
      } else {
        await createMortalityRecord(pondId, formData);
      }
      handleCloseModal();
      fetchMortalityRecords();
    } catch (err) {
      setError("Failed to save mortality record");
    }
  };

  const getRiskColor = (risk: number) => {
    if (risk > 0.7) return "error";
    if (risk > 0.4) return "warning";
    return "success";
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100px">
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      {/* Manual Mortality Records */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">{t("Manual Mortality Records")}</Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpenModal()} disabled={!isCycleActive}>
          {t("Add Mortality Record")}
        </Button>
      </Box>
      {!isCycleActive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("Viewing closed cycle data. Editing is disabled.")}
        </Alert>
      )}
      <TableContainer component={Paper} sx={{overflowX: "auto", maxWidth: "100%"}}>
        <Table size="small" sx={{minWidth: 500}}>
          <TableHead>
            <TableRow>
              <TableCell>{t("Date")}</TableCell>
              <TableCell>{t("Count")}</TableCell>
              <TableCell>{t("Weight (kg)")}</TableCell>
              <TableCell>{t("Cause")}</TableCell>
              <TableCell>{t("Remark")}</TableCell>
              <TableCell>{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mortalityRecords.map((record) => (
              <TableRow key={record.id}>
                <TableCell>{record.recordDate ? new Date(record.recordDate).toLocaleDateString() : "-"}</TableCell>
                <TableCell>{record.count}</TableCell>
                <TableCell>{record.weight}</TableCell>
                <TableCell>{record.cause}</TableCell>
                <TableCell sx={{maxWidth: 150, wordWrap: "break-word"}}>{record.remark || ""}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => handleOpenModal(record)} disabled={!isCycleActive}>
                    {t("Edit")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>{editingRecord ? t("Edit Mortality Record") : t("Add Mortality Record")}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={t("Date")}
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              InputLabelProps={{shrink: true}}
            />
            <Box display="flex" gap={2}>
              <TextField
                label={t("Age (days)")}
                type="number"
                value={formData.age}
                onChange={(e) => setFormData({...formData, age: parseInt(e.target.value) || 0})}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>{t("Based On")}</InputLabel>
                <Select value={formData.basedOn} onChange={(e) => setFormData({...formData, basedOn: e.target.value})} label={t("Based On")}>
                  <MenuItem value="Count">{t("Count")}</MenuItem>
                  <MenuItem value="Weight">{t("Weight")}</MenuItem>
                  <MenuItem value="Size">{t("Size")}</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                label={t("Quantity (pieces)")}
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({...formData, quantity: parseInt(e.target.value) || 0})}
                fullWidth
              />
              <TextField
                label={t("Weight (kg)")}
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData({...formData, weight: parseFloat(e.target.value) || 0})}
                fullWidth
              />
              <TextField
                label={t("Size (cm)")}
                type="number"
                value={formData.size}
                onChange={(e) => setFormData({...formData, size: parseFloat(e.target.value) || 0})}
                fullWidth
              />
            </Box>
            <TextField
              label={t("Multiplier")}
              type="number"
              value={formData.multiplier}
              onChange={(e) => setFormData({...formData, multiplier: parseFloat(e.target.value) || 1})}
              helperText={t("Prediction multiplier for potential mortality")}
            />
            <TextField
              label={t("Notes")}
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder={t("Additional details about the mortality event")}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>{t("Cancel")}</Button>
          <Button onClick={handleSave} variant="contained">
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MortalityTab;
